import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { ETIQUETA_DE_MODULO, type Modulo } from '@/components/layout/modulos';

/**
 * El encabezado de una pantalla de Compite (arquitectura §5): la píldora con el
 * módulo, el título grande, una frase de qué es la pantalla y las acciones a la
 * derecha. Un `volver` opcional arriba, para las pantallas a las que se entra
 * desde otra (Revisar vínculos).
 */
export function PageHeader({
  titulo,
  subtitulo,
  etiqueta,
  icono: Icono,
  acciones,
  volver,
  tonoEtiqueta = 'primario',
  modulo,
  className,
}: {
  titulo: ReactNode;
  subtitulo?: ReactNode;
  /** La píldora de arriba («Compite · Monitoreo»). */
  etiqueta?: ReactNode;
  icono?: LucideIcon;
  acciones?: ReactNode;
  volver?: { texto: string; onClick: () => void };
  /**
   * El color de la píldora (rediseño del 18-oct, artefacto): `primario` en los
   * módulos; `neutro` (gris) en Mi empresa y en los datos de la cuenta.
   */
  tonoEtiqueta?: 'primario' | 'neutro';
  /**
   * La píldora rellena con el color del módulo, como el artefacto («Comercia»
   * en dorado). Manda sobre `tonoEtiqueta`.
   */
  modulo?: Modulo;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-x-6 gap-y-4', className)}>
      <div className="min-w-0 space-y-3">
        {/* En su propia línea: al lado de la píldora se leían como una sola cosa. */}
        {volver && (
          <div>
            <button
              type="button"
              onClick={volver.onClick}
              className="inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
              {volver.texto}
            </button>
          </div>
        )}
        {etiqueta && (
          <EtiquetaDePagina icono={Icono} tono={tonoEtiqueta} modulo={modulo}>
            {etiqueta}
          </EtiquetaDePagina>
        )}
        {/*
          El título grande de los bocetos: 34 px y apretado. En Poppins 600, como el
          artefacto (18-oct): Poppins 800 no se carga y el navegador la inventaba.
        */}
        <h1 className={CLASE_TITULO_DE_PAGINA}>{titulo}</h1>
        {subtitulo && <p className={CLASE_SUBTITULO_DE_PAGINA}>{subtitulo}</p>}
      </div>
      {acciones && <div className="flex flex-wrap items-center gap-2">{acciones}</div>}
    </header>
  );
}

/**
 * La píldora de arriba del título («Cotiza», «Mi empresa · Datec SRL»), sola:
 * para las pantallas que arman su propio encabezado (muchas acciones, filtros)
 * y quieren la misma píldora que `PageHeader`.
 */
export function EtiquetaDePagina({
  children,
  icono: Icono,
  tono = 'primario',
  modulo,
}: {
  children: ReactNode;
  icono?: LucideIcon;
  tono?: 'primario' | 'neutro';
  modulo?: Modulo;
}) {
  return (
    <p
      className={cn(
        'inline-flex h-8 items-center gap-2 rounded-full pl-1.5 pr-3.5 text-[13px] font-semibold',
        modulo
          ? ETIQUETA_DE_MODULO[modulo]
          : tono === 'neutro'
            ? 'border bg-card text-muted-foreground'
            : 'bg-primary/10 text-primary'
      )}
    >
      {Icono && (
        <span
          className={cn(
            'grid h-5 w-5 place-items-center rounded-full',
            modulo ? 'bg-black/10' : 'bg-card'
          )}
        >
          <Icono aria-hidden="true" className="h-3 w-3" />
        </span>
      )}
      {children}
    </p>
  );
}

/** Las clases del título grande y de la frase de abajo, para los encabezados propios. */
export const CLASE_TITULO_DE_PAGINA = 'text-[34px] font-semibold leading-[1.1] tracking-[-0.025em]';
export const CLASE_SUBTITULO_DE_PAGINA = 'text-[15px] text-muted-foreground';
